import {
  BarChart3Icon,
  CloudIcon,
  CloudLightningIcon,
  GaugeIcon,
  GlobeIcon,
  GraduationCapIcon,
  ThermometerSunIcon,
  WindIcon,
  EarthIcon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type Module = {
  number: string;
  title: string;
  subtitle: string;
  context: string;
  items: string[];
  result: string;
  icon: LucideIcon;
};

export const modules: Module[] = [
  {
    number: '1',
    title: 'Fundamentals of Meteorology & Atmospheric Science: The Foundation',
    subtitle: 'The foundation your students need to understand everything else',
    context:
      'Your first introduction to meteorology can be transformative or tedious. This volume transforms technical concepts into something comprehensible and relevant from day one.',
    items: [
      'What is meteorology and why it matters for society',
      'Atmospheric structure: troposphere, stratosphere, and beyond',
      'Atmospheric composition and gases',
      'Fundamental meteorological concepts and variables',
      'Weather vs. climate distinction',
      'Atmospheric pressure and density',
      'Methods used in meteorological research',
      'Clear explanations with real-world examples',
    ],
    result:
      'Your students understand meteorology as a practical science that explains weather patterns and climate systems — not an impossible list of terms to memorize.',
    icon: EarthIcon,
  },
  {
    number: '2',
    title: 'Atmospheric Energy, Temperature & Thermodynamics: How Weather Happens',
    subtitle: 'Everything your students need to know about energy flow and atmospheric heating',
    context:
      'This is where many students struggle. This volume gives you the materials to help them truly understand how solar energy drives atmospheric processes.',
    items: [
      "Solar radiation and Earth's energy balance",
      'Atmospheric absorption and reflection',
      'Heat transfer mechanisms: conduction, convection, radiation',
      'Temperature: measurement, scales, and variation',
      'Atmospheric stability and instability',
      'Pressure systems and barometric changes',
      'Adiabatic processes and temperature changes',
      'Thermodynamic equilibrium and disequilibrium',
      '15 worked energy-balance calculation examples',
    ],
    result: 'Your students learn to understand atmospheric thermodynamics with method and confidence.',
    icon: ThermometerSunIcon,
  },
  {
    number: '3',
    title: 'Clouds, Precipitation & Atmospheric Moisture: Water in Motion',
    subtitle: 'How to teach cloud processes and precipitation clearly and practically',
    context:
      'Clouds and precipitation are fascinating but complex. This volume gives you everything needed so your students understand how water transforms in the atmosphere.',
    items: [
      'Humidity and atmospheric moisture',
      'Dew point and saturation',
      'Condensation nuclei and cloud formation',
      'Cloud types and classification',
      'Fog formation and characteristics',
      'Precipitation processes: collision and coalescence',
      'Precipitation types: rain, snow, sleet, hail',
      'Orographic and frontal precipitation',
      'Precipitation measurement methods',
      'Atmospheric moisture cycles',
      '10 practical case studies ready for classroom use',
    ],
    result: 'Your students understand how clouds form, why it rains, and the role of moisture in weather systems.',
    icon: CloudIcon,
  },
  {
    number: '4',
    title: 'Atmospheric Circulation, Air Masses & Weather Systems: Global Patterns',
    subtitle: 'Understanding wind systems and weather development',
    context:
      'Atmospheric circulation and weather systems are complex but fundamental. This volume breaks them down without losing clarity.',
    items: [
      'Wind formation and the Coriolis effect',
      'Pressure gradients and wind direction',
      'Global wind patterns: trade winds, westerlies, polar easterlies',
      'Jet streams and upper-level flow',
      'Local and regional wind systems',
      'Air mass characteristics and classification',
      'Frontal systems and weather change',
      'Cyclones and anticyclones',
      'High and low pressure systems',
      'Weather system development and movement',
      'Seasonal circulation patterns',
      '12 practical weather system case studies',
    ],
    result: 'Your students understand why winds blow, how air masses clash, and how weather systems develop and move.',
    icon: WindIcon,
  },
  {
    number: '5',
    title: "Severe Weather & Extreme Atmospheric Events: Nature's Fury",
    subtitle: 'Everything about hazardous atmospheric phenomena',
    context:
      'Severe weather captures student interest. This volume gives you confidence to teach thunderstorms, tornadoes, and hurricanes clearly.',
    items: [
      'Thunderstorm development and structure',
      'Lightning, hail, and severe precipitation',
      'Tornado formation and characteristics',
      'Tornado hazards and safety',
      'Tropical cyclone (hurricane) formation',
      'Hurricane structure and intensity',
      'Extreme precipitation and flooding',
      'Heat waves and cold waves',
      'Severe wind events and derechos',
      'Atmospheric hazards and impacts',
      'Forecasting severe weather',
      'Climate change and extreme events',
      '8 severe weather case studies',
    ],
    result: "Your students understand how severe weather forms and why it's dangerous.",
    icon: CloudLightningIcon,
  },
  {
    number: '6',
    title: 'Meteorological Observation, Instruments & Weather Data: Measuring the Atmosphere',
    subtitle: 'Introduction to weather observations and instruments',
    context:
      'Understanding how meteorologists collect data is crucial. This volume teaches observation methods clearly.',
    items: [
      'Weather observation stations',
      'Thermometers and temperature measurement',
      'Barometers and pressure measurement',
      'Anemometers and wind speed measurement',
      'Hygrometers and humidity measurement',
      'Rain gauges and precipitation measurement',
      'Atmospheric instruments and sensors',
      'Data collection and recording methods',
      'Measurement principles and accuracy',
      'Remote sensing and radar',
      'Weather maps and data visualization',
      'Interpretation of weather information',
      'Practical weather observations',
      '10 meteorological measurement case studies',
    ],
    result: 'Your students understand how meteorologists gather and interpret weather data.',
    icon: GaugeIcon,
  },
  {
    number: '7',
    title: 'Weather Analysis & Forecasting: Predicting the Future',
    subtitle: 'Developing foundational weather forecasting skills',
    context: 'Weather forecasting is engaging and practical. This volume teaches analysis and prediction methods.',
    items: [
      'Atmospheric patterns and pressure systems',
      'Weather map interpretation',
      'Surface and upper-level analysis',
      'Frontal analysis and movement',
      'Numerical weather prediction basics',
      'Forecast model types and outputs',
      'Forecast uncertainty and confidence',
      'Forecast verification and skill',
      'Short-range and medium-range forecasting',
      'Seasonal forecasting concepts',
      'Evidence-based forecasting principles',
      'Common forecasting errors',
      'Weather prediction practice',
      '10 real-world forecasting case studies',
    ],
    result: 'Your students understand how meteorologists analyze weather and make forecasts.',
    icon: BarChart3Icon,
  },
  {
    number: '8',
    title: 'Climate, Climate Variability & Global Atmospheric Processes: Long-Term Patterns',
    subtitle: 'Exploring climate systems and large-scale atmospheric interactions',
    context:
      'This is the practical heart. Your students learn how climate works, varies, and connects to global systems.',
    items: [
      'Climate definition and classification systems',
      'Global atmospheric circulation and climate zones',
      'Ocean-atmosphere interactions',
      'El Niño and La Niña phenomena',
      'Monsoons and seasonal wind patterns',
      'Climate variability: natural cycles',
      'Climate change: causes and evidence',
      'Greenhouse gases and radiative forcing',
      'Climate impacts on weather and ecosystems',
      'Climate tipping points and feedback mechanisms',
      'Climate modeling and predictions',
      'Mitigation and adaptation strategies',
      '15 real-world climate case studies',
    ],
    result:
      'Your students understand climate systems and how human activities influence planetary weather patterns.',
    icon: GlobeIcon,
  },
  {
    number: '9',
    title: 'Teaching Meteorology & Atmospheric Science: Classroom Excellence',
    subtitle: 'Your complete guide as a meteorology educator',
    context:
      'This volume gives you exactly what you need to plan, organize, and evaluate your entire year of meteorology teaching.',
    items: [
      'The educational value of meteorology in science curricula',
      'Establishing clear learning objectives',
      'Organizing meteorology content by difficulty level',
      'Designing effective lesson sequences',
      'Inquiry-based meteorology education approaches',
      'Problem-based and project-based learning strategies',
      'Using real weather case studies in the classroom',
      'Teaching through local weather events',
      'Interdisciplinary connections and links',
      'Classroom discussions and guided debates',
      'Laboratory activities and weather observations',
      'Field activities and weather monitoring',
      'Assessment strategies for meteorology learning',
      'Rubrics and performance criteria',
      'Addressing common meteorology misconceptions',
      'Differentiation and inclusive instruction',
      '10 complete integrated meteorology projects',
    ],
    result:
      'You\'ll never wonder "what do I teach today?" again. Everything is planned and contextualized for maximum student learning.',
    icon: GraduationCapIcon,
  },
];
